import { useCallback, useMemo } from 'react'

import { useResetUpdateMutationsCache, useUpdateEventTypeMutation } from 'features/event_type_editor/api'
import { useEventType } from 'features/event_type_provider'
import { useUpsertUserGuideStepMutation } from 'features/user_guide'

import {
  getEventTypeLocks,
  getFormatValuesToServerData,
  getInitialValues,
  getMoreOptionsPanelKeys,
  getPanelFieldsMapping,
  getValidationSchema,
  valuesMapper
} from './utils'
import { useGetDefaultAvailabilityRules } from '../../../utils'
import { BaseSection } from '../base_section'
import { useDescriptionAnalytics } from '../components/description'
import { Panels } from '../panels'

export const MoreOptions = ({ showPaymentsUpfront = false }) => {
  const eventType = useEventType()
  const defaultAvailabilityRules = useGetDefaultAvailabilityRules()
  const [updateEventType, { isLoading, error }] = useUpdateEventTypeMutation({ id: eventType.id })
  const [upsertUserGuideStep] = useUpsertUserGuideStepMutation()

  const panelKeys = useMemo(
    () => getMoreOptionsPanelKeys({ ...eventType, showPaymentsUpfront }),
    [eventType, showPaymentsUpfront]
  )
  const etLocks = getEventTypeLocks(eventType, { showPaymentsUpfront })
  const lockingDisables = Object.values(etLocks).every(Boolean)

  const { handleDescriptionChangeOnSave } = useDescriptionAnalytics()

  const onSuccess = useCallback(() => {
    if (!eventType.isMETParent) {
      upsertUserGuideStep({ identifier: 'customize_and_share_first_et', status: 'completed' })
    }
  }, [eventType.isMETParent, upsertUserGuideStep])

  const handleSubmit = useCallback((attributes) => {
    if (isLoading) return Promise.resolve()

    handleDescriptionChangeOnSave()

    return updateEventType({
      eventType,
      attributes,
      onSuccess
    })
  }, [eventType, handleDescriptionChangeOnSave, isLoading, onSuccess, updateEventType])

  useResetUpdateMutationsCache({ id: eventType.id })

  return (
    <BaseSection
      formatValues={getFormatValuesToServerData({ eventType, showPaymentsUpfront })}
      initialValues={getInitialValues({ eventType, defaultAvailabilityRules, showPaymentsUpfront })}
      panelFieldsMapping={getPanelFieldsMapping({ eventType, showPaymentsUpfront })}
      validationSchema={getValidationSchema(eventType, { showPaymentsUpfront })}
      valuesMapper={valuesMapper}
      externalError={error}
      onSubmit={handleSubmit}
    >
      <Panels
        panelKeys={panelKeys}
        etLocks={etLocks}
        lockingDisables={lockingDisables}
      />
    </BaseSection>
  )
}
